import { CandidateFieldFeatures } from "@bbj-types/organisation/enum";
import { ExtendedCandidateAdditionalField } from "@complex/ExtendedCandidateAdditionalField";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Divider, IconButton, Stack } from "@mui/material";
import { StackGrid } from "@styleguide";
import type { Dispatch, ReactNode } from "react";
import type { IAddParticipantFilters, TAddParticipantFiltersAction } from "../types";
import { type TAdditionalFilterOption, isCandidateSchemaFilterOption, useAdditionalFilters } from "./logics";
import { AdditionalFiltersMenu } from "./menu";

export interface IAdditionalFiltersProps {
  values: IAddParticipantFilters;
  dispatch: Dispatch<TAddParticipantFiltersAction>;
  children: ReactNode;
}

const canDeleteFilter = (option: TAdditionalFilterOption): boolean =>
  !isCandidateSchemaFilterOption(option) || !(option.features & CandidateFieldFeatures.SPOTLIGHT);

export const AdditionalFilters = ({ values, dispatch, children }: IAdditionalFiltersProps) => {
  const {
    options,
    selectedOptions,
    handleSelectOption,
    handleDeleteOption,
    handleChangeExtendedField,
    handleChangeAdditionalField,
  } = useAdditionalFilters(dispatch);

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <StackGrid alignItems="start" perLine={3} rowGap={2} columnGap={2} strict>
        {children}
        {selectedOptions.map((option) => (
          <Stack
            key={option.value}
            direction="row"
            sx={{
              gap: 1,
              alignItems: "start",
            }}
          >
            <ExtendedCandidateAdditionalField
              key={option.value}
              option={option}
              additionnalFieldValue={values.additionalFilters[option.value]}
              extendedFieldValue={values[option.value]}
              onChangeAdditionalField={(value) => handleChangeAdditionalField(option, value)}
              onChangeExtendedField={(value) => handleChangeExtendedField(option, value)}
            />
            {canDeleteFilter(option) && (
              <IconButton size="small" onClick={() => handleDeleteOption(option)}>
                <DeleteOutlinedIcon color="primary" />
              </IconButton>
            )}
          </Stack>
        ))}
      </StackGrid>
      <AdditionalFiltersMenu options={options} onSelectOption={handleSelectOption} />
      <Divider sx={(theme) => ({ borderColor: theme.palette.border.main })} />
    </Stack>
  );
};
